import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  UploadSimple,
  FileText,
  CheckCircle,
  Warning,
  DownloadSimple,
  ArrowRight,
  ArrowsLeftRight,
  CircleNotch,
  XCircle,
  ListBullets,
} from '@phosphor-icons/react';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { ContactListCombobox, NO_LIST, type ContactListSelection } from '../../../components/ui/ContactListCombobox';
import { StepIndicator } from '../../../components/ui/StepIndicator';
import { useContactStore } from '../../../stores/contactStore';
import { useScrollLock } from '../../../lib/useScrollLock';
import { api } from '../../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';

interface ImportContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'upload' | 'mapping' | 'result';

const DB_FIELD_LABELS: Record<string, string> = {
  email: 'Email',
  first_name: 'First Name',
  last_name: 'Last Name',
  job_title: 'Job Title',
  company_name: 'Company',
  phone: 'Phone',
  linkedin_url: 'LinkedIn URL',
  city: 'City',
  state: 'State',
  country: 'Country',
  department: 'Department',
  seniority_level: 'Seniority',
  company_website: 'Website',
  company_industry: 'Industry',
  tags: 'Tags',
  twitter_url: 'Twitter URL',
  company_size: 'Company Size',
  company_revenue: 'Revenue',
  timezone: 'Timezone',
};

// Fields that MUST be mapped (and present in every row) to import — mirrors
// the manual Add Contact form's required fields. Kept in sync with the backend.
const REQUIRED_FIELDS = ['email', 'first_name', 'last_name', 'job_title', 'company_name'];

const STEPS: { key: Step; label: string }[] = [
  { key: 'upload', label: 'Upload' },
  { key: 'mapping', label: 'Map fields' },
  { key: 'result', label: 'Done' },
];

export function ImportContactsModal({ isOpen, onClose }: ImportContactsModalProps) {
  useScrollLock(isOpen);
  const { importContacts, downloadTemplate, contactLists, fetchContactLists } = useContactStore();

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [updateDuplicates, setUpdateDuplicates] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [importing, setImporting] = useState(false);

  // Add-to-list selection: an existing list, a new (typed) name, or none.
  const [listSel, setListSel] = useState<ContactListSelection>(NO_LIST);

  // Load lists when the modal opens so the picker is populated.
  useEffect(() => {
    if (isOpen) fetchContactLists();
  }, [isOpen, fetchContactLists]);

  // Mapping state
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<string[][]>([]);
  const [dbFields, setDbFields] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [previewLoading, setPreviewLoading] = useState(false);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
  }, []);
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
  }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) validateAndSetFile(droppedFile);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) validateAndSetFile(selectedFile);
  };

  const validateAndSetFile = (selectedFile: globalThis.File) => {
    const validExts = ['.csv', '.xlsx', '.xls', '.txt'];
    if (!validExts.some(ext => selectedFile.name.toLowerCase().endsWith(ext))) {
      toast.error('Please upload a CSV, Excel, or TXT file');
      return;
    }
    setFile(selectedFile);
    setImportResult(null);
    setStep('upload');
  };

  const handlePreviewHeaders = async () => {
    if (!file) return;
    setPreviewLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/contacts/preview-headers', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // We render our own toast below (friendly malformed-file message);
        // skip the interceptor's generic toast to avoid a duplicate.
        skipErrorToast: true,
      } as any);
      const data = res.data;
      setFileHeaders(data.headers);
      setSampleRows(data.sample_rows);
      setDbFields(data.db_fields);
      setMapping(data.suggested_mapping || {});
      setStep('mapping');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to read file headers');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file || importing) return;

    if (missingRequired.length > 0) {
      const labels = missingRequired.map(f => DB_FIELD_LABELS[f] || f).join(', ');
      toast.error(`Map all required fields before importing: ${labels}`);
      return;
    }

    setImporting(true);
    try {
      const result = await importContacts(file, {
        skip_duplicates: skipDuplicates,
        update_duplicates: updateDuplicates,
        column_mapping: mapping,
        ...(listSel.mode === 'new'
          ? { new_list_name: listSel.name }
          : listSel.mode === 'existing'
            ? { list_id: listSel.id }
            : {}),
      });
      setImportResult(result);
      setStep('result');
      if (result.status === 'completed') {
        toast.success(`Successfully imported ${result.successful_imports} contacts!`);
      } else {
        toast.warning('Import completed with some issues');
      }
    } catch (error: any) {
      console.error('Import failed:', error);
      const msg = error.response?.data?.detail || error.message || 'Failed to import contacts';
      toast.error(msg, { duration: 6000 });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    setStep('upload');
    setFileHeaders([]);
    setSampleRows([]);
    setMapping({});
    setSkipDuplicates(true);
    setUpdateDuplicates(false);
    setListSel(NO_LIST);
    onClose();
  };

  const updateMapping = (fileHeader: string, dbField: string) => {
    setMapping(prev => ({ ...prev, [fileHeader]: dbField }));
  };

  const mappedCount = Object.values(mapping).filter(v => v && v !== '').length;
  const mappedDbFields = Object.values(mapping);
  const missingRequired = REQUIRED_FIELDS.filter(f => !mappedDbFields.includes(f));
  const allRequiredMapped = missingRequired.length === 0;

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  return createPortal((
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            'w-full rounded-2xl border bg-card shadow-2xl flex flex-col max-h-[90vh]',
            'border-border/60 dark:bg-[#191a1b] dark:border-white/[0.08]',
            'dark:shadow-[0_24px_64px_rgba(0,0,0,0.65),0_4px_12px_rgba(0,0,0,0.45),inset_0_0_0_1px_rgba(255,255,255,0.02)]'
          )}
          style={{ maxWidth: step === 'mapping' ? '56rem' : '40rem' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-border/50 dark:border-white/[0.06] shrink-0">
            <div className="flex items-center gap-4 min-w-0">
              <h2
                className="text-foreground dark:text-[#f7f8f8] truncate"
                style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px', lineHeight: 1.3 }}
              >
                Import contacts
              </h2>
              <StepIndicator steps={STEPS} current={step} className="hidden sm:flex" />
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-5 overflow-y-auto">
            {step === 'upload' && (
              <div className="space-y-5">
                {/* Template hint */}
                <div className="rounded-lg border border-[#5e6ad2]/20 bg-[#5e6ad2]/[0.06] dark:border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.08] p-4 flex items-start gap-3">
                  <div className="h-7 w-7 rounded-md flex items-center justify-center bg-[#5e6ad2]/12 dark:bg-[#5e6ad2]/[0.18] flex-shrink-0">
                    <DownloadSimple className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-foreground dark:text-[#f7f8f8]"
                      style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', lineHeight: 1.5 }}
                    >
                      Use any CSV, Excel, or TXT file
                    </p>
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] mt-0.5"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
                    >
                      We'll help you map columns to contact fields in the next step.
                    </p>
                    <div className="flex items-center gap-3 mt-2.5">
                      <button
                        onClick={() => { downloadTemplate('csv'); toast.success('CSV template downloaded'); }}
                        className="text-[#5e6ad2] hover:text-[#7170ff] dark:text-[#a5b4fc] dark:hover:text-[#c5cbff] underline-offset-2 hover:underline transition-colors"
                        style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                      >
                        Download CSV template
                      </button>
                      <span className="h-3 w-px bg-[#5e6ad2]/25 dark:bg-[#5e6ad2]/30" aria-hidden />
                      <button
                        onClick={() => { downloadTemplate('xlsx'); toast.success('Excel template downloaded'); }}
                        className="text-[#5e6ad2] hover:text-[#7170ff] dark:text-[#a5b4fc] dark:hover:text-[#c5cbff] underline-offset-2 hover:underline transition-colors"
                        style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                      >
                        Download Excel template
                      </button>
                    </div>
                  </div>
                </div>

                {/* Drop zone */}
                <div
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={cn(
                    'border-2 border-dashed rounded-xl p-6 sm:p-8 text-center transition-colors duration-150',
                    isDragging
                      ? 'border-[#7170ff]/60 bg-[#5e6ad2]/[0.05] dark:bg-[#5e6ad2]/[0.08]'
                      : 'border-border/60 hover:border-border/80 hover:bg-muted/30 dark:border-white/[0.08] dark:hover:border-white/[0.14] dark:hover:bg-white/[0.02]'
                  )}
                >
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept=".csv,.xlsx,.xls,.txt"
                    onChange={handleFileSelect}
                  />

                  {file ? (
                    <div className="space-y-3">
                      <div className="h-14 w-14 rounded-xl bg-emerald-500/10 dark:bg-emerald-500/[0.12] dark:border dark:border-emerald-500/20 flex items-center justify-center mx-auto">
                        <FileText className="h-6 w-6 text-emerald-600 dark:text-emerald-400" weight="regular" />
                      </div>
                      <p
                        className="text-foreground dark:text-[#f7f8f8] truncate"
                        style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
                      >
                        {file.name}
                      </p>
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                      >
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                      <button
                        onClick={() => setFile(null)}
                        className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] underline-offset-2 hover:underline transition-colors"
                        style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="h-14 w-14 rounded-xl bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto">
                        <UploadSimple className="h-6 w-6 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                      </div>
                      <p
                        className="text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
                      >
                        Drag and drop your file here
                      </p>
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98]"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                      >
                        or{' '}
                        <label
                          htmlFor="file-upload"
                          className="text-[#5e6ad2] hover:text-[#7170ff] dark:text-[#a5b4fc] dark:hover:text-[#c5cbff] cursor-pointer underline-offset-2 hover:underline transition-colors"
                          style={{ fontWeight: 510 }}
                        >
                          browse files
                        </label>
                      </p>
                      <p
                        className="text-muted-foreground/80 dark:text-[#62666d]"
                        style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                      >
                        CSV, Excel, or TXT · max 10MB
                      </p>
                    </div>
                  )}
                </div>

                {/* Add to list — featured suggestive search */}
                <div className="space-y-2.5 rounded-lg border border-[#5e6ad2]/25 bg-[#5e6ad2]/[0.05] dark:border-[#5e6ad2]/30 dark:bg-[#5e6ad2]/[0.07] p-3">
                  <div className="flex items-center gap-2">
                    <ListBullets className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="bold" />
                    <span
                      className="text-foreground dark:text-[#f7f8f8]"
                      style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      Add to list{' '}
                      <span className="text-muted-foreground dark:text-[#62666d]" style={{ fontWeight: 400 }}>
                        (optional)
                      </span>
                    </span>
                  </div>
                  <ContactListCombobox
                    lists={contactLists.map((l) => ({ id: l.id, name: l.name }))}
                    value={listSel}
                    onChange={setListSel}
                    allowCreate
                    allowClear
                    emphasized
                    placeholder="Search a list, or type a new name…"
                    ariaLabel="Add imported contacts to a list"
                  />
                  {listSel.mode !== 'none' && (
                    <p
                      className="text-muted-foreground dark:text-[#62666d]"
                      style={{ fontSize: '11px', letterSpacing: '-0.05px', lineHeight: 1.45 }}
                    >
                      {listSel.mode === 'new'
                        ? `A new list "${listSel.name}" will be created and all imported contacts added to it.`
                        : 'All imported contacts, including existing duplicates, are added to this list.'}
                    </p>
                  )}
                </div>

                {/* Import options */}
                <div className="space-y-2 rounded-lg border border-border/50 bg-muted/30 dark:bg-white/[0.02] dark:border-white/[0.06] p-3">
                  <OptionRow
                    checked={skipDuplicates}
                    onChange={(c) => { setSkipDuplicates(c); if (c) setUpdateDuplicates(false); }}
                    title="Skip duplicates"
                    description="Don't import contacts with existing emails"
                  />
                  <OptionRow
                    checked={updateDuplicates}
                    onChange={(c) => { setUpdateDuplicates(c); if (c) setSkipDuplicates(false); }}
                    title="Update duplicates"
                    description="Merge new data into existing contacts"
                  />
                </div>
              </div>
            )}

            {step === 'mapping' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <p
                    className="text-muted-foreground dark:text-[#8a8f98]"
                    style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
                  >
                    Map your file columns to contact fields.{' '}
                    <span
                      className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                      style={{ fontWeight: 590 }}
                    >
                      {mappedCount}
                    </span>{' '}
                    of{' '}
                    <span
                      className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                      style={{ fontWeight: 590 }}
                    >
                      {fileHeaders.length}
                    </span>{' '}
                    mapped.
                  </p>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border',
                      allRequiredMapped
                        ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/25'
                        : 'bg-red-500/10 text-red-600 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/25'
                    )}
                    style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                    title={allRequiredMapped ? undefined : `Required: ${missingRequired.map(f => DB_FIELD_LABELS[f] || f).join(', ')}`}
                  >
                    {allRequiredMapped
                      ? <CheckCircle className="h-3 w-3" weight="fill" />
                      : <Warning className="h-3 w-3" weight="fill" />}
                    {allRequiredMapped
                      ? 'All required fields mapped'
                      : `Required: ${missingRequired.map(f => DB_FIELD_LABELS[f] || f).join(', ')}`}
                  </span>
                </div>

                {/* Mapping table */}
                <div className="rounded-lg border border-border/50 dark:border-white/[0.08] overflow-hidden">
                  <div className="max-h-[420px] overflow-y-auto">
                    <table className="w-full">
                      <thead className="sticky top-0 z-10 bg-muted/40 backdrop-blur-sm dark:bg-[#191a1b]/80 dark:backdrop-blur">
                        <tr className="border-b border-border/50 dark:border-white/[0.06]">
                          <th
                            className="px-4 py-2.5 text-left text-muted-foreground dark:text-[#8a8f98] uppercase w-[28%]"
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                          >
                            File column
                          </th>
                          <th className="px-2 py-2.5 w-8" />
                          <th
                            className="px-4 py-2.5 text-left text-muted-foreground dark:text-[#8a8f98] uppercase w-[30%]"
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                          >
                            Maps to
                          </th>
                          <th
                            className="px-4 py-2.5 text-left text-muted-foreground dark:text-[#8a8f98] uppercase"
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                          >
                            Sample data
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50 dark:divide-white/[0.06]">
                        {fileHeaders.map((header, idx) => {
                          const currentMapping = mapping[header] || '';
                          const samples = sampleRows.map(row => row[idx]).filter(v => v && v !== '');
                          const selectOptions = [
                            { value: '', label: '— Skip —' },
                            ...dbFields.map((field) => {
                              const alreadyUsed = Object.entries(mapping).some(
                                ([h, v]) => v === field && h !== header
                              );
                              return {
                                value: alreadyUsed ? '' : field,
                                label: alreadyUsed
                                  ? `${DB_FIELD_LABELS[field] || field} (already mapped)`
                                  : (DB_FIELD_LABELS[field] || field),
                              };
                            }),
                          ];

                          return (
                            <tr
                              key={header}
                              className={cn(
                                'transition-colors duration-150',
                                currentMapping
                                  ? 'bg-emerald-500/[0.04] dark:bg-emerald-500/[0.05]'
                                  : 'hover:bg-muted/30 dark:hover:bg-white/[0.03]'
                              )}
                            >
                              <td className="px-4 py-2.5">
                                <code
                                  className="inline-block px-2 py-0.5 rounded bg-muted/60 dark:bg-white/[0.04] dark:border dark:border-white/[0.06] text-foreground dark:text-[#f7f8f8] truncate max-w-[180px]"
                                  title={header}
                                  style={{ fontSize: '11px', fontFamily: 'ui-monospace, SF Mono, Menlo, monospace', letterSpacing: '-0.05px' }}
                                >
                                  {header}
                                </code>
                              </td>
                              <td className="px-2 py-2.5 text-center">
                                <ArrowRight
                                  className={cn(
                                    'h-3.5 w-3.5 mx-auto transition-colors duration-150',
                                    currentMapping
                                      ? 'text-emerald-600 dark:text-emerald-400'
                                      : 'text-muted-foreground/40 dark:text-[#3e3e44]'
                                  )}
                                  weight="bold"
                                />
                              </td>
                              <td className="px-4 py-2.5">
                                <Select
                                  value={currentMapping}
                                  onChange={(v) => updateMapping(header, v)}
                                  options={selectOptions}
                                  placeholder="— Skip —"
                                  ariaLabel={`Map ${header} to`}
                                  className="w-full"
                                  buttonClassName={cn(
                                    'h-8',
                                    currentMapping &&
                                      'border-emerald-500/30 dark:border-emerald-500/30'
                                  )}
                                />
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="flex gap-1.5 overflow-hidden flex-wrap">
                                  {samples.slice(0, 2).map((s, i) => (
                                    <span
                                      key={i}
                                      className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border/50 truncate max-w-[140px] dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06]"
                                      style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                                      title={s}
                                    >
                                      {s}
                                    </span>
                                  ))}
                                  {samples.length === 0 && (
                                    <span
                                      className="text-muted-foreground/50 dark:text-[#3e3e44] italic"
                                      style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                                    >
                                      empty
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {step === 'result' && importResult && (
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  {importResult.status === 'completed' ? (
                    <div className="h-12 w-12 rounded-md bg-emerald-500/10 dark:bg-emerald-500/[0.12] dark:border dark:border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-emerald-600 dark:text-emerald-400" weight="regular" />
                    </div>
                  ) : (
                    <div className="h-12 w-12 rounded-md bg-amber-500/10 dark:bg-amber-500/[0.12] dark:border dark:border-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <Warning className="h-6 w-6 text-amber-600 dark:text-amber-400" weight="regular" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3
                      className="text-foreground dark:text-[#f7f8f8]"
                      style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px', lineHeight: 1.3 }}
                    >
                      Import complete
                    </h3>
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] truncate mt-0.5"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                    >
                      {importResult.file_name}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: 'Total rows', value: importResult.total_rows, tone: 'neutral' as const },
                    { label: 'Imported', value: importResult.successful_imports, tone: 'success' as const },
                    { label: 'Duplicates', value: importResult.duplicate_emails, tone: 'warning' as const },
                    { label: 'Failed', value: importResult.failed_imports, tone: 'error' as const },
                  ].map((s) => (
                    <StatTile key={s.label} label={s.label} value={s.value} tone={s.tone} />
                  ))}
                </div>

                {importResult.list_name && importResult.added_to_list != null && (
                  <div className="rounded-lg border border-[#5e6ad2]/20 bg-[#5e6ad2]/[0.06] dark:border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.08] p-3 flex items-center gap-2.5">
                    <ListBullets className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc] flex-shrink-0" weight="bold" />
                    <p
                      className="text-foreground dark:text-[#f7f8f8]"
                      style={{ fontSize: '13px', letterSpacing: '-0.1px', lineHeight: 1.45 }}
                    >
                      Added{' '}
                      <span className="tabular-nums" style={{ fontWeight: 590 }}>{importResult.added_to_list}</span>
                      {' '}contact{importResult.added_to_list === 1 ? '' : 's'} to{' '}
                      <span style={{ fontWeight: 590 }}>{importResult.list_name}</span>
                    </p>
                  </div>
                )}

                {importResult.error_log?.errors && importResult.error_log.errors.length > 0 && (
                  <div className="rounded-lg border border-red-500/20 bg-red-500/[0.04] dark:border-red-500/25 dark:bg-red-500/[0.06] p-4">
                    <h4
                      className="text-foreground dark:text-[#f7f8f8] mb-2 inline-flex items-center gap-1.5"
                      style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                    >
                      <XCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" weight="fill" />
                      Errors
                    </h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {importResult.error_log.errors.slice(0, 10).map((error: any, idx: number) => (
                        <p
                          key={idx}
                          className="text-muted-foreground dark:text-[#8a8f98]"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
                        >
                          <span className="tabular-nums text-foreground/80 dark:text-[#d0d6e0]">Row {error.row}:</span> {error.error}
                        </p>
                      ))}
                      {importResult.error_log.errors.length > 10 && (
                        <p
                          className="text-muted-foreground/80 dark:text-[#62666d] italic mt-1"
                          style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                        >
                          And {importResult.error_log.errors.length - 10} more…
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 px-6 py-3.5 border-t border-border/50 dark:border-white/[0.06] bg-muted/20 dark:bg-white/[0.01] shrink-0">
            <div>
              {step === 'mapping' && (
                <Button variant="ghost" size="sm" onClick={() => setStep('upload')}>
                  Back
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2.5">
              <Button variant="outline" size="sm" onClick={handleClose}>
                {step === 'result' ? 'Close' : 'Cancel'}
              </Button>

              {step === 'upload' && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handlePreviewHeaders}
                  disabled={!file || previewLoading}
                  icon={
                    previewLoading
                      ? <CircleNotch className="h-4 w-4 animate-spin" />
                      : <ArrowsLeftRight className="h-4 w-4" weight="bold" />
                  }
                >
                  {previewLoading ? 'Reading…' : 'Map columns'}
                </Button>
              )}

              {step === 'mapping' && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleImport}
                  loading={importing}
                  disabled={!allRequiredMapped}
                  icon={<UploadSimple className="h-4 w-4" weight="bold" />}
                >
                  Import {mappedCount > 0 ? `· ${mappedCount} fields` : ''}
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  ), document.body);
}

// ─── Option row (checkbox + copy) ───────────────────────────────────────────
function OptionRow({
  checked,
  onChange,
  title,
  description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  description: string;
}) {
  return (
    <label
      className={cn(
        'flex items-start gap-3 px-2 py-1.5 rounded-md cursor-pointer transition-colors duration-150',
        'hover:bg-muted/40 dark:hover:bg-white/[0.03]'
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-3.5 w-3.5 rounded-sm border-border/60 dark:border-white/[0.12] accent-[#5e6ad2]"
      />
      <div className="min-w-0">
        <div
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
        >
          {title}
        </div>
        <div
          className="text-muted-foreground dark:text-[#8a8f98] mt-0.5"
          style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.45 }}
        >
          {description}
        </div>
      </div>
    </label>
  );
}

// ─── Stat tile (result step) ────────────────────────────────────────────────
function StatTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'neutral' | 'success' | 'warning' | 'error';
}) {
  const valueColor = {
    neutral: 'text-foreground dark:text-[#f7f8f8]',
    success: 'text-emerald-600 dark:text-emerald-400',
    warning: 'text-amber-600 dark:text-amber-400',
    error: 'text-red-600 dark:text-red-400',
  }[tone];

  return (
    <div className="rounded-lg border border-border/50 bg-card dark:bg-white/[0.02] dark:border-white/[0.08] p-3 text-center">
      <p
        className={cn('tabular-nums', valueColor)}
        style={{ fontSize: '22px', fontWeight: 590, letterSpacing: '-0.22px', lineHeight: 1.1 }}
      >
        {value}
      </p>
      <p
        className="text-muted-foreground dark:text-[#8a8f98] uppercase mt-1"
        style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '0.08em' }}
      >
        {label}
      </p>
    </div>
  );
}
